// SKU identity helpers — two flavors.
//
//   productKey()         — name + brand + category + size. Comparison-safe
//                          across the 2026-05-18 sku_hash cutover. Use for
//                          any logic that pairs today's row with a row
//                          from a different scraped_date (alerts feed,
//                          dashboard widget, new-sale detection).
//   productKeyWithHash() — adds sku_hash. Use only for same-day
//                          disambiguation where two physically-different
//                          SKUs share every other column (Haze Saratoga
//                          Explicit "Honeydew Squeeze [1g]" $7 regular vs.
//                          $28 Diamond Infused premium, etc.).
//
// Why the split — going-forward scrapes stamp sku_hash with a source-side
// id (Dutchie GraphQL `id`, iHeartJane objectID, Treez /item/<slug>) as
// e.g. "dutchie:abc123". Backfilled pre-2026-05-18 rows got a content-
// hash fallback "fb:<sha256>". The two formats never collide for the
// same physical product, so any cross-day key that includes sku_hash
// silently zeros out a feed whose window straddles the cutover. Drop the
// segment in the comparison key; keep it where strict identity matters.

// Normalize a string segment for use in a key: lowercase, trimmed, empty
// string for null/undefined/empty. Keeps key matching tolerant of casing.
function norm(v) {
  if (v == null) return '';
  return String(v).toLowerCase().trim();
}

// Build a stable size segment from the structured columns on a row. Returns
// "<unit>|<value>|<pack>" with empty strings for missing parts so two rows
// with the same partial information still collide consistently.
export function sizeKey(row) {
  if (!row) return '||';
  const v = row.size_value != null && row.size_value !== '' ? String(row.size_value) : '';
  const u = row.size_unit ? String(row.size_unit).toLowerCase().trim() : '';
  const p = row.pack_count != null && row.pack_count !== '' ? String(row.pack_count) : '';
  return `${u}|${v}|${p}`;
}

// Cross-day comparison key. `nameField` lets callers point at
// `product_name` (price_history) or `name` (products) without renaming
// first. Brand and category come off the row when present; an absent
// column falls back to empty-string segments.
export function productKey(row, nameField = 'product_name') {
  const name = row[nameField] ?? row.name ?? '';
  return `${row.store_id ?? ''}|${name}|${norm(row.brand)}|${norm(row.category)}|${sizeKey(row)}`;
}

// Strict same-day identity key. Adds the sku_hash segment so two
// physically-different products that share every other column don't
// collapse onto the same key. NOT safe for cross-day comparison — see
// header comment.
export function productKeyWithHash(row, nameField = 'product_name') {
  return `${productKey(row, nameField)}|${row.sku_hash ?? ''}`;
}

// Cutover-stable form of a sku_hash for use in cross-day comparison keys.
//
// sku_hash is "<platform>:<source_id>[:<weight_token>]" (lib/sku.py). The
// per-platform scrapers began appending the trailing weight token at
// different dates (iHeartJane 2026-05-27, Dutchie 2026-06-09), so the SAME
// physical SKU's hash silently gained a ":gram" / ":each" / ":7g" suffix
// partway through its history. The source_id in segment 2 is what actually
// distinguishes two SKUs that share a name+brand+category+size — e.g. Cake
// House's Raw Garden "Italian Soda 1g" exists as both a Rosin
// (iheartjane:2694028) and a Live Resin (iheartjane:2751058) — and no
// source_id contains a colon, so the first two segments are a complete,
// stable identity. Dropping the weight token costs nothing for
// disambiguation (rows sharing a productKey already share a size, hence the
// same token) while keeping the hash equal across that suffix cutover.
// fb:<sha256> fallback hashes have only two segments and pass through.
export function stableSkuHash(hash) {
  if (!hash) return '';
  const parts = String(hash).split(':');
  return parts.length >= 3 ? `${parts[0]}:${parts[1]}` : hash;
}

// Cross-day comparison key that ALSO separates two physically-different
// SKUs sharing every identity column. Use in place of productKey() wherever
// a same-name/size collision would let one variant's price masquerade as
// another's across days (price-alerts feed, dashboard diff, new-sale flag).
//
// Unlike productKeyWithHash(), this is safe across days because it appends
// the cutover-stable stableSkuHash() rather than the raw sku_hash. The one
// remaining gap: the pre-2026-05-18 fb:<sha256> backfill format still
// differs from the going-forward platform ids, so a window straddling THAT
// cutover would not pair start to end. Harmless for the current ≤3-week
// feeds, whose earliest reachable date is well after 2026-05-18.
export function productKeyStable(row, nameField = 'product_name') {
  return `${productKey(row, nameField)}|${stableSkuHash(row.sku_hash)}`;
}

// Format the structured size for display. Returns "" for fully-empty rows.
export function formatSize(row) {
  if (!row) return '';
  const v = row.size_value;
  const u = row.size_unit;
  const p = row.pack_count;
  if (p && p > 1 && v && u) return `${p}x${v}${u}`;
  if (v && u) return `${v}${u}`;
  if (v) return String(v);
  return '';
}
